import { AxiosError } from 'axios'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import ProgressBar from 'ext-ui/components/ProgressBar/ProgressBar'
import CardIconSvg from 'ext-ui/svg/illustration/CardIconSvg'
import ShieldIconSvg from 'ext-ui/svg/illustration/ShieldIconSvg'
import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { usePutCreditCardV2 } from 'src/api/mutation/creditcards'
import { useLazyGetCreditCardById } from 'src/api/query/creditcards'
import { PusherContext } from 'src/app/App'
import { AstradaBinData } from 'src/context/AstradaContext/AstradaContext'
import { PusherEvents } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { PhysicalCardStatus } from 'src/models/creditCard.models'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import { selectPusherChannelPrefix } from 'src/redux/selectors/session.selectors'

import Confirm3DSVerification from './Confirm3DSVerification'
import style from './PhysicalCardVerifyStep.style'

// Defense-in-depth against a stuck spinner. The spinner is shown whenever
// `verificationId === ''` — i.e. while we're waiting for the PUT response or for the Pusher
// `UPDATED` event that delivers the verificationId (or transitions the card to ACTIVE /
// NEEDS_CVS_VERIFICATION). 60s matches the backend's IntegratorService Astrada-call timeout,
// so by the time this fires the backend has either responded or hit its own timeout — meaning
// we'll never false-positive while the backend is legitimately still working.
const ENROLLMENT_TIMEOUT_MS = 60_000

type PhysicalCardVerifyStepProps = {
  className?: string
  cardId: string
  eligibilityType: AstradaBinData['eligibility']
  /**
   * Use this callback for card enrollment errors when creating the stub
   * card via the /physical endpoint, or when the card fails via Pusher event
   * during the creation process
   */
  onCardEnrollError?: (error: string) => void
  onVerifyStart?: () => void
  /**
   * Use this callback for when 3ds verification fails
   */
  onVerifyError?: (errorMessage: string) => void
  onVerifyComplete?: (cardId: string) => void
}

/**
 * Render this component to start the 3DS verification process.
 * This is a 'Batteries Included' component which does our entire
 * 3DS automation flow
 *
 */
export default function PhysicalCardVerifyStep(
  props: PhysicalCardVerifyStepProps,
) {
  const {
    className = '',
    cardId,
    onCardEnrollError,
    onVerifyStart,
    onVerifyError,
    onVerifyComplete,
  } = props
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()
  const { mutateAsync: putCreditCardV2 } = usePutCreditCardV2()
  const pusher = useContext(PusherContext)
  const [verificationId, setVerificationId] = useState<string>('')
  const [failureCount, setFailureCount] = useState<number>(0)

  const userId = useSelector(selectUserIdFromState)
  const pusherChannelPrefix = useSelector(selectPusherChannelPrefix)

  const { getCardById } = useLazyGetCreditCardById()

  // Track when 3DS iframe is shown
  useEffect(() => {
    if (verificationId) {
      analytics.track('3ds.verificationStarted')
    }
  }, [verificationId, analytics])

  // Start enrollment on mount - runs exactly once
  useEffect(() => {
    const startVerification = async () => {
      const creditCard = await getCardById(cardId)
      if (
        creditCard.physicalCard.status === PhysicalCardStatus.NeedsVerification
      ) {
        const freshVerificationId = creditCard.physicalCard.verificationId
        if (freshVerificationId) {
          setVerificationId(freshVerificationId)
          return
        }
      }

      onVerifyStart?.()
      putCreditCardV2({
        creditCardId: cardId,
        endpoint: 'physical',
      })
        .then((response) => {
          if (response?.creditCard?.verificationId) {
            setVerificationId(response.creditCard.verificationId)
          }
          // Otherwise, Pusher UPDATED will trigger a refetch to get the verificationId
        })
        .catch((error) => {
          const unwrappedError = error as AxiosError
          const errorData = unwrappedError.response?.data as { error?: string }
          const defaultErrorMessage = translate(
            'Card enrollment failed - Try again later',
          )
          onCardEnrollError?.(errorData.error || defaultErrorMessage)
        })
    }

    startVerification()
  }, [])

  // Pusher event handler - handles UPDATED and FAILED actions
  useEffect(() => {
    if (!pusher) return

    const channel = pusher.subscribe(`${pusherChannelPrefix}${userId}`)

    const handlePhysicalAccount = async (data: {
      id: string
      action: string
    }) => {
      // Ignore events for other cards
      if (data.id !== cardId) return

      if (data.action === 'UPDATED') {
        // Fetch card to check current status - backend is source of truth
        const creditCard = await getCardById(cardId)
        const status = creditCard.physicalCard?.status

        // These statuses indicate 3DS verification succeeded
        if (
          status === PhysicalCardStatus.Active ||
          status === PhysicalCardStatus.NeedsCVSVerification
        ) {
          onVerifyComplete?.(cardId)
        } else if (
          status === PhysicalCardStatus.NeedsVerification &&
          creditCard.physicalCard?.verificationId
        ) {
          // Still awaiting 3DS - set verificationId to show the 3DS iframe
          setVerificationId(creditCard.physicalCard.verificationId)
        }
      }

      if (data.action === 'FAILED') {
        onCardEnrollError?.('ERROR_FAILED')
      }
    }

    channel.bind(PusherEvents.PhysicalAccount, handlePhysicalAccount)

    return () => {
      channel.unbind(PusherEvents.PhysicalAccount, handlePhysicalAccount)
    }
  }, [
    pusher,
    pusherChannelPrefix,
    userId,
    cardId,
    getCardById,
    onVerifyComplete,
    onCardEnrollError,
  ])

  // Spinner timeout. While we're showing the spinner (verificationId === ''), arm a timer that
  // surfaces an error if neither the PUT response nor a Pusher event resolves within
  // ENROLLMENT_TIMEOUT_MS. Re-armed each time we re-enter the spinner state (e.g. after the
  // user completes 3DS and we're waiting for the Pusher confirmation).
  useEffect(() => {
    if (verificationId !== '') return

    const timeoutId = window.setTimeout(() => {
      onCardEnrollError?.('ENROLLMENT_TIMEOUT')
    }, ENROLLMENT_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [verificationId, onCardEnrollError])

  // Message event handler for 3DS popup window
  // Handles both success (to show loading) and failure states
  // Actual success is confirmed by backend status via Pusher UPDATED event
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data.type === '3ds.verificationComplete') {
        // 3DS verification succeeded in iframe - clear verificationId to show
        // loading spinner while waiting for Pusher confirmation
        analytics.track('3ds.verificationComplete', {
          flowType: event.data.flowType,
        })
        setVerificationId('')
        return
      }

      if (event.data.type === '3ds.verificationFailed') {
        analytics.track('3ds.verificationFailed', {
          flowType: event.data.flowType,
        })
        const totalFailureCount = failureCount + 1
        if (totalFailureCount >= 2) {
          // Display an error state and stop trying
          // This 'ERROR_FAILED' string is a constant that will trigger a generic message
          // in an AlertPanel component to show up on the previous screen
          onVerifyError?.('ERROR_FAILED')
          return
        }
        setVerificationId('')
        setFailureCount(totalFailureCount)
      }
    }

    window.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [failureCount, analytics, onVerifyError])

  return (
    <div className={cn('PhysicalCardVerifyStep', className)} css={style}>
      {verificationId ? (
        <Confirm3DSVerification
          verificationId={verificationId}
          isRetry={failureCount > 0}
        />
      ) : (
        <div className="flex-column align-center justify-center gap-10">
          <div className="cardIcon-background flex-column align-center justify-center">
            <CardIconSvg />
          </div>

          <ProgressBar variant="indeterminate" />
          <div className="profit flex-column gap-3">
            <p className="font-h3">
              {`🎨  ${translate('Painting profit rainbows...')}`}
            </p>
            <p className="add-color">
              {translate('Adding color to your portfolio')}
            </p>
          </div>
          <AlertPanel>
            <div className="flex-row align-start gap-3">
              <ShieldIconSvg />
              <div className="flex-column gap-2">
                <p className="font-bold">{translate('Secure Processing')}</p>
                <p className="alert-text">
                  {translate(
                    'Your card information is encrypted and processed securely. We never store your full card details.',
                  )}
                </p>
              </div>
            </div>
          </AlertPanel>
        </div>
      )}
    </div>
  )
}
